import { getOrCreateConnection, releaseConnection } from "@/game/services/ConnectionCache";
import type { EventDataType, GameEventTypeMap } from "@/game/types/gameEvents";
import { GameLogger } from "@/game/utils/gameLogger";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Hook to subscribe to game events
 *
 * @param gameId - The ID of the game to subscribe to (can be null)
 * @returns An object with functions to subscribe and unsubscribe from events
 */
export function useGameEvents(gameId: string | null) {
  // Track subscriptions for cleanup
  const subscriptionsRef = useRef<(() => void)[]>([]);

  // Cleanup helper
  const cleanupSubscriptions = useCallback(() => {
    subscriptionsRef.current.forEach((unsubscribe) => unsubscribe());
    subscriptionsRef.current = [];
  }, []);

  // Subscribe to an event with automatic cleanup
  const subscribe = useCallback(
    <T extends keyof GameEventTypeMap>(
      eventType: T,
      callback: (data: EventDataType<T>) => void
    ): (() => void) | undefined => {
      if (!gameId) return undefined;

      const connInfo = getOrCreateConnection(gameId);
      connInfo.refCount++; // Increment for this subscription

      const handler = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);

          // Skip if already processed (animation deduplication)
          // Use atomic comparison and update to prevent race conditions
          if (data.seq && data.seq <= connInfo.lastSequence) {
            return;
          }

          // Only update if this sequence is actually newer
          if (data.seq && data.seq > connInfo.lastSequence) {
            connInfo.lastSequence = data.seq;
          }

          callback(data);
        } catch (err) {
          GameLogger.logError("useGameEvents", `Failed to parse ${eventType}:`, err);
        }
      };

      // Use native addEventListener
      connInfo.source.addEventListener(eventType as string, handler);

      const unsubscribe = () => {
        connInfo.source.removeEventListener(eventType as string, handler);
        releaseConnection(gameId);
      };

      subscriptionsRef.current.push(unsubscribe);
      return unsubscribe;
    },
    [gameId]
  );

  // Manually unsubscribe from a specific event
  const unsubscribe = useCallback(
    <T extends keyof GameEventTypeMap>(
      eventType: T,
      callback: (data: EventDataType<T>) => void
    ) => {
      // This implementation is kept for API compatibility but is rarely used
      // The auto-cleanup via subscribe() return value is the primary pattern
      if (!gameId) return;

      GameLogger.logWarn(
        "useGameEvents",
        "Direct unsubscribe() is deprecated. Use the returned unsubscribe function from subscribe()"
      );
    },
    [gameId]
  );

  // Cleanup all subscriptions when component unmounts or gameId changes
  useEffect(() => {
    return () => {
      cleanupSubscriptions();
    };
  }, [gameId, cleanupSubscriptions]);

  // Function to explicitly close the connection (e.g., when game ends)
  const closeConnection = useCallback(() => {
    if (gameId) {
      cleanupSubscriptions();
    }
  }, [gameId, cleanupSubscriptions]);

  return {
    subscribe,
    unsubscribe,
    closeConnection
  };
}

/**
 * Simplified hook to get data from a specific event type
 *
 * @param gameId - The ID of the game to subscribe to
 * @param eventType - The type of event to subscribe to
 * @returns The latest data received for this event type
 */
export function useGameEventData<T extends keyof GameEventTypeMap>(
  gameId: string | null,
  eventType: T
) {
  const [data, setData] = useState<EventDataType<T> | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const { subscribe } = useGameEvents(gameId);

  useEffect(() => {
    if (!gameId) return;

    setLoading(true);

    const cleanup = subscribe(eventType, (newData: EventDataType<T>) => {
      setData(newData as EventDataType<T>);
      setLoading(false);
      setError(null);
    });

    // Return cleanup function
    return cleanup;
  }, [gameId, eventType, subscribe]);

  return { data, loading, error };
}
